import { useSyncExternalStore } from "react";

/**
 * Хранилище «тяжёлой» части окон модалок — React-узлов и колбэков — вне Redux.
 *
 * Зачем:
 *   - В RTK reducers используется Immer (`createSlice`). Если положить
 *     React.ReactNode в стейт, Immer пытается рекурсивно обходить дерево
 *     props/children/options и легко уходит в "Maximum call stack size exceeded"
 *     на больших/циклических графах (см. finalize.ts → finalizeProperty → each).
 *   - Колбэки (`onDimmerClick`) в Redux тоже антипаттерн — они не сериализуемы
 *     и ломают RTK serializableCheck.
 *
 * Решение: в Redux держим только метаданные окна `{ uid, dimmer }`, а сам
 * `content` и `onDimmerClick` лежат здесь, в обычной `Map`. Подписка точечная,
 * по `uid`, через `useSyncExternalStore`, чтобы перерендер ловил только тот,
 * чей контент реально поменялся.
 */

export interface ModalContentEntry {
	content: React.ReactNode;
	onDimmerClick?: () => void;
}

type Listener = () => void;

const entries = new Map<number, ModalContentEntry>();
const listeners = new Map<number, Set<Listener>>();

const notify = (uid: number) => {
	const set = listeners.get(uid);
	if (!set) return;
	set.forEach((listener) => {
		try {
			listener();
		} catch {
			/* перерендер другой подписки не должен ронять остальные */
		}
	});
};

export const setModalContent = (
	uid: number,
	entry: ModalContentEntry,
): void => {
	entries.set(uid, entry);
	notify(uid);
};

export const removeModalContent = (uid: number): void => {
	if (!entries.has(uid)) return;
	entries.delete(uid);
	notify(uid);
};

export const getModalContent = (
	uid: number,
): ModalContentEntry | undefined => entries.get(uid);

const subscribe = (uid: number, listener: Listener): (() => void) => {
	let set = listeners.get(uid);
	if (!set) {
		set = new Set();
		listeners.set(uid, set);
	}
	set.add(listener);
	return () => {
		const current = listeners.get(uid);
		if (!current) return;
		current.delete(listener);
		if (current.size === 0) listeners.delete(uid);
	};
};

/**
 * Хук для `ModalController`: подписывается на изменения контента окна `uid`
 * и возвращает текущую запись (или `undefined`, если окно уже удалили).
 */
export const useModalContent = (
	uid: number,
): ModalContentEntry | undefined => {
	const getSnapshot = () => entries.get(uid);
	return useSyncExternalStore(
		(listener) => subscribe(uid, listener),
		getSnapshot,
		getSnapshot,
	);
};
